'use client';

import { useEffect, useRef } from 'react';

export const RadialGradientBackground = ({
  fadeIn,
  productPage,
  repeating = false,
  size,
}: {
  fadeIn?: boolean;
  productPage?: boolean;
  repeating?: boolean;
  size: 'sm' | 'md' | 'lg';
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const fadeInClass = fadeIn ? 'animate-fade-in' : '';

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Respect the OS-level motion preference outright: no canvas, no RAF loop,
    // no per-frame work at all for these users. This is the only path that
    // costs nothing across every device tier, so it's checked before anything
    // else — matching how AnimatedTitleVideo already treats reduced motion.
    const reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    if (reducedMotionQuery.matches) return;

    // Initial sizing. After this, `resizeCanvas` only touches the bitmap when
    // the size really changed: assigning width/height WIPES the canvas even
    // when the value is identical (per spec), and mobile browsers fire
    // `resize` for every URL-bar collapse/expand and keyboard toggle — the
    // unconditional assignment blanked the (dark-mode-only) starfield on
    // every scroll direction change until the next RAF got around to
    // repainting, which read as the whole background flashing while moving.
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;
    const resizeCanvas = () => {
      if (canvas.width === window.innerWidth && canvas.height === window.innerHeight) return;
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
      // Repaint in the same task — a wiped bitmap must never reach the screen.
      drawFrame();
    };
    window.addEventListener('resize', resizeCanvas);

    // Particle configuration based on size
    const isMobile = window.innerWidth < 768;
    const particleCount = isMobile 
      ? (size === 'sm' ? 17 : size === 'md' ? 25 : 43)
      : (size === 'sm' ? 30 : size === 'md' ? 50 : 80);
    // Mobile keeps the same particle count as before, but drops the two
    // expensive per-frame steps below (all-pairs connection lines and a
    // per-particle radial gradient glow) — those are O(n^2) and heap-
    // allocation heavy respectively, and are what actually cost CPU, far
    // more than the particle count itself. Desktop keeps the full effect.
    const showConnectionsAndGlow = !isMobile;

    class Particle {
      x: number;
      y: number;
      vx: number;
      vy: number;
      radius: number;
      opacity: number;
      pulseSpeed: number;
      pulsePhase: number;

      constructor(canvasWidth: number, canvasHeight: number) {
        this.x = Math.random() * canvasWidth;
        this.y = Math.random() * canvasHeight;
        this.vx = (Math.random() - 0.5) * 0.3;
        this.vy = (Math.random() - 0.5) * 0.3;
        this.radius = Math.random() * 2 + 1;
        this.opacity = Math.random() * 0.5 + 0.3;
        this.pulseSpeed = Math.random() * 0.02 + 0.01;
        this.pulsePhase = Math.random() * Math.PI * 2;
      }

      update(canvasWidth: number, canvasHeight: number) {
        this.x += this.vx;
        this.y += this.vy;

        // Wrap around edges
        if (this.x < 0) this.x = canvasWidth;
        if (this.x > canvasWidth) this.x = 0;
        if (this.y < 0) this.y = canvasHeight;
        if (this.y > canvasHeight) this.y = 0;

        // Pulse effect
        this.pulsePhase += this.pulseSpeed;
      }

      draw(isDarkMode: boolean) {
        if (!ctx) return;
        
        const pulse = Math.sin(this.pulsePhase) * 0.3 + 0.7;
        const alpha = this.opacity * pulse;

        // Draw particle
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.radius, 0, Math.PI * 2);
        ctx.fillStyle = isDarkMode
          ? `rgba(100, 149, 237, ${alpha})`
          : `rgba(20, 50, 220, ${alpha * 0.7})`;
        ctx.fill();

        if (!showConnectionsAndGlow) return;

        // Draw glow
        const gradient = ctx.createRadialGradient(this.x, this.y, 0, this.x, this.y, this.radius * 4);
        gradient.addColorStop(0, isDarkMode 
          ? `rgba(100, 149, 237, ${alpha * 0.3})` 
          : `rgba(20, 50, 220, ${alpha * 0.25})`);
        gradient.addColorStop(1, isDarkMode 
          ? 'rgba(100, 149, 237, 0)'
          : 'rgba(20, 50, 220, 0)');
        ctx.fillStyle = gradient;
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.radius * 4, 0, Math.PI * 2);
        ctx.fill();
      }
    }

    const particles: Particle[] = [];
    for (let i = 0; i < particleCount; i++) {
      particles.push(new Particle(canvas.width, canvas.height));
    }

    // Animation loop. drawFrame is standalone so resizeCanvas can repaint
    // synchronously after a genuine resize instead of leaving a blank frame.
    const drawFrame = () => {
      if (!ctx || !canvas) return;

      ctx.clearRect(0, 0, canvas.width, canvas.height);

      const isDarkMode = document.documentElement.classList.contains('dark');

      // Draw connections between nearby particles (desktop only — see
      // showConnectionsAndGlow above).
      if (showConnectionsAndGlow) {
        particles.forEach((particle, i) => {
          particles.slice(i + 1).forEach((otherParticle) => {
            const dx = particle.x - otherParticle.x;
            const dy = particle.y - otherParticle.y;
            const distance = Math.sqrt(dx * dx + dy * dy);

            if (distance < 150) {
              ctx.beginPath();
              ctx.moveTo(particle.x, particle.y);
              ctx.lineTo(otherParticle.x, otherParticle.y);
              const alpha = (1 - distance / 150) * 0.2;
              ctx.strokeStyle = isDarkMode
                ? `rgba(100, 149, 237, ${alpha})`
                : `rgba(20, 50, 220, ${alpha * 0.6})`;
              ctx.lineWidth = 1;
              ctx.stroke();
            }
          });
        });
      }

      // Update and draw particles
      particles.forEach((particle) => {
        particle.update(canvas.width, canvas.height);
        particle.draw(isDarkMode);
      });
    };

    let animationId: number | null = null;
    // Phones paint at ~30fps: the drift is far too slow for the difference to
    // read, and every skipped frame skips a full-viewport canvas raster +
    // layer upload — GPU budget that matters on the devices where the OS
    // kills over-budget tabs (a forced reload dumps the visitor back at the
    // top of the page).
    const frameIntervalMs = isMobile ? 33 : 0;
    let lastPaintTime = -Infinity;
    const animate = (time: number = performance.now()) => {
      if (!frameIntervalMs || time - lastPaintTime >= frameIntervalMs) {
        lastPaintTime = time;
        drawFrame();
      }
      animationId = requestAnimationFrame(animate);
    };

    // The loop only actually runs while ALL of these hold: the page is dark
    // themed (the container is CSS-hidden otherwise, so drawing is wasted
    // work), the tab is visible, and the canvas itself is on-screen. Each
    // condition is watched independently and toggles the same start/stop
    // pair, so e.g. switching themes or backgrounding the tab immediately
    // pauses the simulation instead of it running invisibly forever.
    let isRunning = false;
    const start = () => {
      if (isRunning) return;
      isRunning = true;
      animate();
    };
    const stop = () => {
      isRunning = false;
      if (animationId !== null) {
        cancelAnimationFrame(animationId);
        animationId = null;
      }
    };

    let isDarkThemeActive = document.documentElement.classList.contains('dark');
    let isTabVisible = !document.hidden;
    let isOnScreen = true; // optimistic until the observer's first callback
    const reconcile = () => {
      if (isDarkThemeActive && isTabVisible && isOnScreen) start();
      else stop();
    };

    const themeObserver = new MutationObserver(() => {
      isDarkThemeActive = document.documentElement.classList.contains('dark');
      reconcile();
    });
    themeObserver.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });

    const handleVisibilityChange = () => {
      isTabVisible = !document.hidden;
      reconcile();
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    const intersectionObserver = new IntersectionObserver(
      ([entry]) => {
        isOnScreen = entry?.isIntersecting ?? true;
        reconcile();
      },
      { threshold: 0 },
    );
    intersectionObserver.observe(canvas);

    reconcile();

    return () => {
      window.removeEventListener('resize', resizeCanvas);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      themeObserver.disconnect();
      intersectionObserver.disconnect();
      stop();
    };
  }, [size]);

  return (
    <>
    <div
      className={`${productPage ? 'max-sm:hidden absolute h-[calc(100vh+7.5rem)] max-sm:h-[calc(100vh+8.5rem)] xl:h-[calc(100vh+9.5rem)]' : 'absolute h-[calc(100vh+7.5rem)] max-sm:h-[calc(100vh+8.5rem)] xl:h-[calc(100vh+9.5rem)]'} top-0 left-0 w-full overflow-hidden pointer-events-none ${fadeInClass} hidden dark:block motion-reduce:hidden`}
    >
      {/* Stars / Constellations */}
      <canvas
        ref={canvasRef}
        className="absolute inset-0 w-full h-full"
        style={{ opacity: 0.6 }}
      />
    </div>
    <div
      className={`${productPage ? 'max-sm:hidden absolute h-search' : 'absolute h-full'} top-0 left-0 w-full overflow-hidden pointer-events-none ${fadeInClass} hidden dark:block motion-reduce:hidden`}
    >
      {/* Animated gradient orb - Center.
          No CSS blur here: blur-3xl on a 700x600 layer whose transform
          animates forced the GPU to keep a huge filtered surface alive for
          the whole 12s pulse loop — enough sustained load on phones for the
          OS to kill and silently reload the tab (visitor lands back at the
          top). A radial gradient with a longer falloff is visually the same
          soft glow, and transform+opacity alone stay on the compositor. */}
      <div
        className={`absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[600px] opacity-70 rounded-full animate-pulse-glow motion-reduce:animate-none`}
        style={{
          background: 'radial-gradient(circle, rgba(80, 147, 235, 0.28) 0%, rgba(80, 147, 235, 0.13) 34%, rgba(80, 147, 235, 0) 68%)',
        }}
      />
      
    </div>
    </>
  );
};
